// What the native apps do for the add form that a web page cannot: load a
// merchant's site (CORS forbids a page to read another site). The phone
// renders the product page and downloads the chosen image, where our server is
// often blocked. Contract and per-platform details: doc/ajout-de-produit.md.
//
// Each function resolves null when the phone cannot help — on the web, or on
// any failure — and the caller falls back to the server, as before. The
// platform transports live here only: no controller knows them.

import { pageReadiness } from './page_readiness'

// The native side bounds its own work (8 s to render a page, 10 s to download
// an image), but a reply that never comes would leave the form spinning
// forever: past this, we go on without the phone.
const TIMEOUT = 20000

function bounded(promise) {
  return Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), TIMEOUT))])
}

// iOS (app and share extension): WKScriptMessageHandlerWithReply, whose
// postMessage returns a Promise.
const ios = (name) => window.webkit?.messageHandlers?.[name]

// Android (app and share sheet): `listyPhone`, injected in Listy pages by
// WebViewCompat.addWebMessageListener. A reply is not tied to its call there:
// each request carries an id, sent back with the answer.
const android = () => window.listyPhone
const pending = new Map()
let sequence = 0

function askAndroid(name, payload) {
  const phone = android()
  phone.onmessage ||= ({ data }) => {
    const { id, error, ...reply } = JSON.parse(data)
    const request = pending.get(id)
    if (!request) return

    pending.delete(id)
    if (error) request.reject(new Error(error))
    else request.resolve(reply)
  }

  return new Promise((resolve, reject) => {
    const id = String(++sequence)
    pending.set(id, { resolve, reject })
    // Forgotten along with the caller's patience (see bounded).
    setTimeout(() => pending.delete(id), TIMEOUT)
    phone.postMessage(JSON.stringify({ id, name, ...payload }))
  })
}

const available = (name) => Boolean(ios(name) || android())

// The phone's reply, as a Promise that rejects when it cannot help.
function ask(name, payload) {
  return ios(name)?.postMessage(payload) ?? askAndroid(name, payload)
}

// The merchant page as rendered on the phone (HTML), or null.
// `refresh`: load the page again rather than serve the one already rendered —
// what the form's re-scrape button asks for.
// `readiness`: when the page is ready to extract (page_readiness.js), as source
// run on the merchant's page; `currencies` are its argument.
export function renderPage(url, { refresh = false, currencies = [] } = {}) {
  if (!available('listyRenderPage')) return Promise.resolve(null)

  const readiness = `(${pageReadiness})(${JSON.stringify(currencies)})`
  return bounded(
    ask('listyRenderPage', { url, refresh, readiness }).then(
      ({ html }) => html,
      () => null
    )
  )
}

export function canDownloadImage() {
  return available('listyDownloadImage')
}

// The image downloaded by the phone, as a File ready for a file input, or null.
// `referer`: the product page, which some CDNs require.
export async function downloadImage(url, referer) {
  if (!canDownloadImage()) return null

  try {
    const image = await bounded(ask('listyDownloadImage', { url, referer }))
    if (!image) return null

    const bytes = Uint8Array.from(atob(image.data), (char) => char.charCodeAt(0))
    return new File([bytes], `cover.${image.type.split('/')[1]}`, { type: image.type })
  } catch {
    return null
  }
}
